import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { FILTER_PRICE } from 'dpl/constants/query_params';
import { pushParams, resetParams } from 'dpl/actions/query_params';
import { LEARNING_CENTER_PRICE_PATH } from 'dpl/shared/constants/urls';
import { beginsWithVowel } from 'dpl/shared/utils';
import SmartLink from 'dpl/components/SmartLink';
import SimpleSelect from 'dpl/form/components/SimpleSelect';

const DEFAULT_PRICE_OPTION = { value: null, title: 'Any' };

export default function PriceFilter({ title, dropdownLabel }) {
  const queryParams = useSelector(state => state.queryParams);
  const { puppyPriceRanges, breedName, medianBreedPrice } = useSelector(
    ({ server }) => server
  );
  const dispatch = useDispatch();

  const priceOption = useMemo(
    () =>
      puppyPriceRanges.find(
        ({ value }) => value === queryParams[FILTER_PRICE]
      ) || DEFAULT_PRICE_OPTION,
    [queryParams[FILTER_PRICE]]
  );

  function handleFilterChange(option) {
    const newPriceOption = option.target.value;

    if (!newPriceOption) {
      dispatch(resetParams(FILTER_PRICE, { hash: true }));
    } else {
      dispatch(pushParams({ [FILTER_PRICE]: newPriceOption }, { hash: true }));
    }
  }

  return (
    <>
      {title && (
        <p className="mb4 font-16 fw-medium md:font-18 md:font-serif">
          {title}
        </p>
      )}
      {/*
          Calculating average price can be inaccurate due to pricing outliers. Using a
          calculated median price based off breed's litter price range low/high values instead.
        */}
      {Boolean(medianBreedPrice) && (
        <p className="f2 mb4 lh-copy">
          The average price of {beginsWithVowel(breedName) ? 'an' : 'a'}{' '}
          {breedName} puppy is {medianBreedPrice}.{' '}
          <SmartLink
            className="underline"
            to={LEARNING_CENTER_PRICE_PATH}
            target="_blank"
          >
            Learn more
          </SmartLink>
        </p>
      )}
      {dropdownLabel && <p className="mb2 font-12 secondary">Price</p>}
      <SimpleSelect
        options={puppyPriceRanges}
        onChange={handleFilterChange}
        placeholder={DEFAULT_PRICE_OPTION.title}
        value={priceOption.value}
      />
    </>
  );
}

PriceFilter.propTypes = {
  title: PropTypes.string,
  dropdownLabel: PropTypes.string
};

PriceFilter.defaultProps = {
  title: null,
  dropdownLabel: null
};
